import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { fetchVideos, deleteVideo, Video, VideoStatus } from '../store/slices/videoSlice';
import { format } from 'date-fns';
import apiService from '../services/api';
import withAuth from '../hooks/withAuth';

// VideoUploader component
import VideoUploader from '../components/VideoUploader';
import EmptyStateMessage from '../components/EmptyStateMessage';
import VideoList from '../components/VideoList';
import { VideoCache } from '../hooks/useVideoData';

// Add a new VideoPreview component
interface VideoPreviewProps {
  originalVideoId: string | null;
  originalVideoUrl: string | null;
  processedVideoUrl: string | null;
  videoStatus: string;
  onProcess: (id: string) => void;
  onClose: () => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ 
  originalVideoId, 
  originalVideoUrl, 
  processedVideoUrl, 
  videoStatus,
  onProcess,
  onClose
}) => {
  const isProcessing = videoStatus === 'Processing';
  
  // Check the URL first to understand its structure
  useEffect(() => {
    if (originalVideoUrl) {
      console.log('VideoPreview received originalVideoUrl:', originalVideoUrl);
    }
  }, [originalVideoUrl]);
  
  const generateVideoUrl = (url: string | null) => {
    if (!url) return null;
    
    console.log(`VideoPreview: Generating URL for ${url}`);
    
    // Extract just the path part if it's a full URL
    if (url.includes('supabase.co/storage') || url.includes('/object/public/')) {
      try {
        const urlParts = url.split('/');
        const filename = urlParts[urlParts.length - 1];
        console.log(`VideoPreview: Extracted filename from URL: ${filename}`);
        // Use the full path as stored in the database
        const generatedUrl = `/api/storage/${url.includes('processed') ? 'processed-videos' : 'original-videos'}/${urlParts[urlParts.length - 1]}`;
        console.log(`VideoPreview: Generated URL for Supabase URL: ${generatedUrl}`);
        return generatedUrl;
      } catch (e) {
        console.error('Error parsing URL:', e);
      }
    }
    
    // Handle different path formats:
    // 1. Just the filename: filename.mp4
    // 2. User ID + filename: userId/filename.mp4
    // 3. User ID + timestamp + filename: userId/timestamp-filename.mp4
    const bucket = url.includes('processed') ? 'processed-videos' : 'original-videos';
    
    // If the URL has a userId prefix (contains '/'), use the full path
    if (url.includes('/')) {
      const generatedUrl = `/api/storage/${bucket}/${url}`;
      console.log(`VideoPreview: Generated URL with userId path: ${generatedUrl}`);
      return generatedUrl;
    }
    
    // For simple filenames, just pass the filename
    const generatedUrl = `/api/storage/${bucket}/${url}`;
    console.log(`VideoPreview: Generated URL for filename: ${generatedUrl}`);
    return generatedUrl;
  };
  
  // Add debug output to render
  const originalVideoUrlForRender = originalVideoUrl ? generateVideoUrl(originalVideoUrl) : null;
  const processedVideoUrlForRender = processedVideoUrl ? generateVideoUrl(processedVideoUrl) : null;
  
  console.log('VideoPreview rendering with URLs:', {
    originalId: originalVideoId,
    original: originalVideoUrlForRender,
    processed: processedVideoUrlForRender,
    status: videoStatus
  });
  
  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Video Preview</Typography>
        <IconButton onClick={onClose} size="small">
          <CancelIcon />
        </IconButton>
      </Box>
      
      <Grid container spacing={2}>
        {/* Original Video */}
        <Grid item xs={12} md={processedVideoUrl ? 6 : 12}>
          <Typography variant="subtitle1" gutterBottom>
            Original Video
          </Typography>
          {originalVideoUrl ? (
            <Box
              component="video"
              sx={{ width: '100%', borderRadius: 1, maxHeight: '300px' }}
              controls
              src={originalVideoUrlForRender}
            />
          ) : (
            <Box sx={{ 
              height: '200px', 
              bgcolor: 'grey.200', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Typography color="text.secondary">No preview available</Typography>
            </Box>
          )}
        </Grid>
        
        {/* Processed Video or Process Button */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Processed Video
          </Typography>
          {processedVideoUrl ? (
            <Box
              component="video"
              sx={{ width: '100%', borderRadius: 1, maxHeight: '300px' }}
              controls
              src={processedVideoUrlForRender}
            />
          ) : (
            <Box sx={{ 
              height: '200px', 
              bgcolor: 'grey.100', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2
            }}>
              <Typography color="text.secondary">
                {isProcessing ? 'Video is being processed...' : 'No processed version yet'}
              </Typography>
              {!isProcessing && originalVideoId && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => originalVideoId && onProcess(originalVideoId)}
                  disabled={isProcessing}
                >
                  Process Video
                </Button>
              )}
              {isProcessing && (
                <Typography variant="caption" color="text.secondary">
                  Processing may take a few minutes
                </Typography>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
      
      {originalVideoId && !processedVideoUrl && !isProcessing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Refresh Status
          </Button>
        </Box>
      )}
    </Paper>
  );
};

// Create a flag to track if we're returning from another page
let isReturningToPage = false;

const VideoManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  // Get videos from Redux store
  const { videos, loading, error } = useSelector((state: RootState) => state.videos);
  
  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  
  // Add state for the last uploaded video
  const [lastUploadedVideo, setLastUploadedVideo] = useState<any>(null);
  
  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // State for upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // Track navigation to avoid duplicate API calls when returning
  useEffect(() => {
    // Clean up stale timers if they exist
    const storedTimerId = sessionStorage.getItem('videoListTimerId');
    if (storedTimerId) {
      clearTimeout(parseInt(storedTimerId));
      sessionStorage.removeItem('videoListTimerId');
    }
    
    // Check if we're returning to the page (not mounting for the first time)
    if (isReturningToPage) {
      console.log("Returning to video management - using cached data if available");
      // Wait a bit before allowing new API calls - prevents rapid successive calls
      const timerId = setTimeout(() => {
        isReturningToPage = false;
      }, 1000);
      
      // Store the timer ID to clean it up if needed
      sessionStorage.setItem('videoListTimerId', timerId.toString());
    } else {
      // First time visit
      isReturningToPage = true;
    }

    return () => {
      // Component is unmounting, mark that we'll be returning
      isReturningToPage = true;
    };
  }, [location.pathname]);
  
  // Show uploader automatically if on the upload route
  useEffect(() => {
    if (location.pathname === '/videos/upload') {
      setShowUploader(true);
    }
  }, [location.pathname]);
  
  // Fetch videos on component mount and handle polling
  useEffect(() => {
    // Initial fetch
    if (!isReturningToPage) {
      dispatch(fetchVideos());
    }
    
    // Set up polling only for processing videos
    const processingVideo = videos.find(video => video.status === 'Processing');
    
    let pollingInterval: NodeJS.Timeout | null = null;
    
    if (processingVideo && lastUploadedVideo?.id === processingVideo.id) {
      pollingInterval = setInterval(() => {
        // Only fetch if not rate limited
        dispatch(fetchVideos()).unwrap()
          .catch(error => {
            if (error === 'Rate limit cooldown') {
              console.log('Skipping poll due to rate limit');
            }
          });
      }, 10000); // Poll every 10 seconds instead of 5
    }
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [dispatch, lastUploadedVideo]);
  
  // Handle "Upload New Video" button click
  const handleUpload = () => {
    setShowUploader(true);
  };
  
  // Handle upload completion
  const handleUploadComplete = (uploadedVideo: any) => {
    setShowUploader(false);
    
    // Ensure the video object has the properly formatted URL path
    // The response has the full path including userId/timestamp-filename
    const enhancedVideo = {
      ...uploadedVideo,
      // If original_url doesn't include the userId, we need to ensure 
      // the complete path is used for the preview component
      original_url: uploadedVideo.original_url
    };
    
    console.log('Uploaded video data:', enhancedVideo);
    setLastUploadedVideo(enhancedVideo);
    
    // Refresh videos list
    dispatch(fetchVideos());
  };
  
  // Handle close preview
  const handleClosePreview = () => {
    setLastUploadedVideo(null);
  };
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  // Handle status filter change
  const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStatusFilter(event.target.value as string);
  };
  
  // Filter videos based on search and status filter
  const filteredVideos = videos.filter(video => {
    const matchesSearch = searchTerm ? (
      (video.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
      (video.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    ) : true;
    
    const matchesStatus = statusFilter === 'All Statuses' || video.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle delete video
  const handleDeleteVideo = (videoId: string) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      dispatch(deleteVideo(videoId))
        .unwrap()
        .then(() => {
          setNotification({
            open: true,
            message: 'Video deleted successfully',
            severity: 'success'
          });
        })
        .catch((error) => {
          setNotification({
            open: true,
            message: `Failed to delete video: ${error}`,
            severity: 'error'
          });
        });
    }
  };
  
  // Handle process video
  const handleProcessVideo = async (videoId: string) => {
    try {
      await apiService.processVideo(videoId);
      
      // Show success notification
      setNotification({
        open: true,
        message: 'Video processing started',
        severity: 'success'
      });
      
      // Refresh the video list after a delay
      setTimeout(() => {
        dispatch(fetchVideos());
      }, 1500);
    } catch (error: any) {
      console.error('Failed to process video:', error);
      setNotification({
        open: true,
        message: `Failed to process video: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };
  
  // Get status chip color based on status
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'Processing':
        return 'warning';
      case 'Preview Ready':
      case 'Published':
        return 'success';
      case 'Error':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Handle notification close
  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Function to navigate to video details
  const handleViewVideo = (videoId: string) => {
    navigate(`/videos/${videoId}`);
  };

  // Handle refresh button click
  const handleRefresh = () => {
    dispatch(fetchVideos()).unwrap()
      .catch(error => {
        if (error === 'Rate limit cooldown') {
          setNotification({
            open: true,
            message: 'Please wait a few seconds before refreshing again',
            severity: 'info'
          });
        }
      });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Video Management
      </Typography>
      
      {/* Upload section */}
      {!showUploader ? (
        <Paper sx={{ p: 3, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload New Video</Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleUpload}
          >
            Upload Video
          </Button>
        </Paper>
      ) : (
        <VideoUploader 
          open={showUploader}
          onClose={() => setShowUploader(false)}
          onUploadComplete={handleUploadComplete} 
        />
      )}
      
      {/* Video preview if we have a recently uploaded video */}
      {lastUploadedVideo && (
        <VideoPreview 
          originalVideoId={lastUploadedVideo.id}
          originalVideoUrl={lastUploadedVideo.original_url}
          processedVideoUrl={lastUploadedVideo.processed_url}
          videoStatus={lastUploadedVideo.status}
          onProcess={handleProcessVideo}
          onClose={handleClosePreview}
        />
      )}
      
      {/* Filters and search */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search videos..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Filter by Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Filter by Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="All Statuses">All Statuses</MenuItem>
                <MenuItem value="Uploaded">Uploaded</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Processing Failed">Processing Failed</MenuItem>
                <MenuItem value="Preview Ready">Preview Ready</MenuItem>
                <MenuItem value="Published">Published</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              fullWidth 
              variant="outlined" 
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Video list */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Uploaded At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Alert severity="error">{error}</Alert>
                  </TableCell>
                </TableRow>
              ) : videos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <EmptyStateMessage 
                      title="No videos found"
                      description="Upload a video to get started"
                      action={
                        <Button 
                          variant="contained" 
                          startIcon={<AddIcon />}
                          onClick={handleUpload}
                        >
                          Upload Video
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                // Filter videos based on search and status
                videos
                  .filter(video => 
                    (video.title || 'Untitled')
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) &&
                    (statusFilter === 'All Statuses' || video.status === statusFilter)
                  )
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((video) => (
                    <TableRow key={video.id} hover>
                      <TableCell>
                        <Button
                          sx={{ 
                            textAlign: 'left', 
                            justifyContent: 'flex-start',
                            fontWeight: 'normal',
                            textTransform: 'none'
                          }}
                          onClick={() => handleViewVideo(video.id)}
                        >
                          {video.title || 'Untitled Video'}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={video.status} 
                          color={getStatusChipColor(video.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {video.created_at ? format(new Date(video.created_at), 'MMM d, yyyy') : 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewVideo(video.id)}
                            title="View video details"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/videos/${video.id}/edit`)}
                            title="Edit video"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteVideo(video.id)}
                            title="Delete video"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          {video.status !== 'Processing' && (
                            <IconButton 
                              size="small" 
                              onClick={() => handleProcessVideo(video.id)}
                              title="Process video"
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          )}
                          {video.status === 'Processing' && (
                            <CircularProgress size={24} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={videos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
      >
        <Alert onClose={handleNotificationClose} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default withAuth(VideoManagement); 
